import Pristine from 'pristinejs'
import VMasker from 'vanilla-masker'

import debounce from '../utils/debounce'
import validate from './validate-fields'
import * as rules from './rules'

/**
 *  @typedef pristineConfig
 *  @type { Object }
 *  @property { string } classTo - class of the parent element where the error/success class is added
 *  @property { string } errorClass - class to error element
 *  @property { string } successClass - class to error success
 *  @property { string } errorTextParent - class of the parent element where error text element is appended
 *  @property { string } errorTextClass - class of the error text element
 *  @property { string } errorTextTag - type of element to create for the error text
 */

/**
 * @typedef ValidatorOptions
 * @type { Object }
 * @property { pristineConfig } pristine - is a Pristine {@link  https://github.com/sha256/Pristine | Configuration }
 */

const DEFAULT = {
  options: {
    pristine: {
      classTo: 'js-pristine',
      errorClass: 'has-error',
      errorTextTag: 'span',
      errorTextParent: 'js-pristine',
      errorTextClass: 'u-pristine-error',
    },
  },
}

const _multiMask = (masks, max, event) => {
  const element = event.target
  const value = element.value.replace(/\D/g, '')
  const mask = element.value.length > max ? 1 : 0
  VMasker(element).unMask()
  VMasker(element).maskPattern(masks[mask])
  element.value = VMasker.toPattern(value, masks[mask])
}

/**
 * @typedef Validator
 * Class responsible for verifying the integrity of data entered into forms.
 */
class Validator {
  /**
   * @constructor
   * @param { HTMLFormElement! } target - is a FORM element
   * @param { ValidatorOptions } options - is a custom configuration to validate
   * @param { boolean } live - is config to set livecheck on type
   */
  constructor(target, options = {}, live = true) {
    if (!target.elementName === 'FORM') {
      throw new Error('That element not is a Form Element.')
    }

    this.form = target
    this.config = Object.assign(DEFAULT.options.pristine, options.pristine)
    this.validator = new Pristine(this.form, this.config, live)
    return this.validator
  }

  /**
   * Name - is a FullName Validator
   * @param { Validator! } instance - Instance of Validator
   */
  static Name(instance, optional) {
    if (!instance) throw new Error('Instance not found in Name validator.')

    const { target, message, priority, halt } = validate.name

    const fields = instance.form.querySelectorAll(target)

    this.ApplyRule(
      fields,
      (field, validator) => {
        validator.addValidator(
          field,
          (value) => {
            return rules.name(value, optional)
          },
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * Email - is a E-mail Validator
   * @param { Validator! } instance - Instance of Validator
   */
  static Email(instance) {
    if (!instance) throw new Error('Instance not found in Email validator.')

    const { target, message, priority, halt } = validate.email

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        validator.addValidator(field, rules.email, message, priority, halt)
      },
      instance
    )
  }

  /**
   * VerifyEmail - is a E-mail Validator
   * @param { Validator! } instance - Instance of Validator
   */
  static VerifyEmail(instance) {
    if (!instance) throw new Error('Instance not found in Email validator.')

    const { target, message, priority, halt } = validate.verifyEmail

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        validator.addValidator(
          field,
          () => rules.verifyEmail(field),
          message,
          priority,
          halt
        )

        field.addEventListener(
          'keyup',
          debounce((event) => {
            rules.checker(instance, event.target, message)
          }, 1000)
        )

        field.addEventListener('blur', (event) => {
          rules.checker(instance, event.target, message)
        })
      },
      instance
    )
  }

  /**
   * Password - is a Password Validator
   * @param { Validator! } instance - Instance of Validator
   * @param { Number } minChar - Set the min value to password, default: 6
   * @param { Number } maxChar - Set the max value to password, default: 12
   */
  static Password(instance, minChar = 6, maxChar = 12) {
    if (!instance) throw new Error('Instance not found in Password validator.')

    const { target, priority, halt } = validate.password

    const fields = instance.form.querySelectorAll(target)

    const min = {
      rule: (value) => value.length >= minChar,
      message: `A senha precisa ter no mínimo ${minChar} caracteres.`,
    }

    const max = {
      rule: (value) => value.length <= maxChar,
      message: `Sua senha deve ter no máximo ${maxChar} caracteres`,
    }

    const equals = {
      rule: (password, confirm) => password === confirm,
      message: 'A senha digitada e a confirmação não são iguais',
    }

    this.ApplyRule(
      fields,
      (field, validator) => {
        validator.addValidator(field, min.rule, min.message, priority, halt)
        validator.addValidator(field, max.rule, max.message, priority, halt)

        const targetValidateIsEquals = field.getAttribute(
          'data-validate-password'
        )
        const targetEquals = document.querySelector(targetValidateIsEquals)

        if (targetEquals) {
          validator.addValidator(
            field,
            (value) => {
              return equals.rule(value, targetEquals.value)
            },
            equals.message,
            priority,
            halt
          )
        }
      },
      instance
    )
  }

  /**
   * CPF - is a CPF Validation
   * @param { Validator! } instance - Instance of Validator
   */
  static CPF(instance) {
    if (!instance) throw new Error('Instance not found in CPF validator.')

    const { target, message, priority, halt, mask } = validate.cpf

    const fields = instance.form.querySelectorAll(target)

    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          VMasker(field).maskPattern(mask)
        }
        validator.addValidator(field, rules.cpf, message, priority, halt)
      },
      instance
    )
  }

  /**
   * Cellphone - is a CellPhone Validation
   * @param { Validator! } instance - Instance of Validator
   */
  static Cellphone(instance) {
    if (!instance) throw new Error('Instance not found in cellphone validator.')

    const { target, message, priority, halt, mask } = validate.cellphone

    const fields = instance.form.querySelectorAll(target)

    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          /**
           * if is a array on masks, use this method to check both formats.
           */
          if (Array.isArray(mask)) {
            field.addEventListener(
              'input',
              _multiMask.bind(undefined, mask, 14),
              false
            )
          } else {
            VMasker(field).maskPattern(mask)
          }
        }

        validator.addValidator(
          field,
          rules.isCellPhone,
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * Birthdate - is a Date of Born Validation
   * @param { Validator! } instance - Instance of Validator
   */
  static Birthdate(instance, date, optional) {
    if (!instance) throw new Error('Instance not found in Birthdate validator.')

    const { target, message, priority, halt, mask } = validate.birthdate

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          VMasker(field).maskPattern(mask)
        }
        validator.addValidator(
          field,
          (value) => {
            return rules.date(value, date, optional)
          },
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * Money - is money validation
   * @param { Validator! } instance - Instance of Validator
   */
  static Money(instance) {
    if (!instance) throw new Error('Instance not found in Money validator.')

    const fields = instance.form.querySelectorAll('[data-validate-money]')

    this.ApplyRule(
      fields,
      (field) => {
        VMasker(field).maskMoney({
          // Decimal precision -> "90"
          precision: 2,
          // Decimal separator -> ",90"
          separator: ',',
          // Number delimiter -> "12.345.678"
          delimiter: '.',
          // Money unit -> "R$ 12.345.678,90"
          unit: 'R$',
          // Force type only number instead decimal,
          // masking decimals with ",00"
          // Zero cents -> "R$ 1.234.567.890,00"
          zeroCents: false,
        })
      },
      instance
    )
  }

  /**
   * MinAge - minimum age validation
   * @param { Validator! } instance - Instance of Validator
   */
  static MinAge(instance, minAge) {
    if (!instance) throw new Error('Instance not found in MinAge validator.')

    let { message } = validate.minage
    const { target, priority, halt, mask } = validate.minage

    if (minAge) {
      message = message.replace(/[0-9]{1,3}/g, minAge)
    }

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          VMasker(field).maskPattern(mask)
        }
        validator.addValidator(
          field,
          (value) => {
            return rules.minAge(value, minAge)
          },
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * MaxAge - maximum age validation
   * @param { Validator! } instance - Instance of Validator
   */
  static MaxAge(instance, maxAge) {
    if (!instance) throw new Error('Instance not found in MaxAge validator.')

    const { target, message, priority, halt, mask } = validate.maxage

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          VMasker(field).maskPattern(mask)
        }
        validator.addValidator(
          field,
          (value) => {
            return rules.maxAge(value, maxAge)
          },
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * Cep - Is validation cep
   * @param { Validator! } instance - Instance of Validator
   */
  static Cep(instance, optional) {
    if (!instance) throw new Error('Instance not found in Cep validator.')

    const { target, message, priority, halt, mask } = validate.cep

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          VMasker(field).maskPattern(mask)
        }
        validator.addValidator(
          field,
          (value) => {
            return rules.cep(value, optional)
          },
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * Emoji - Is validation emoji
   * @param { Validator! } instance - Instance of Validator
   */
  static Emoji(instance) {
    if (!instance) throw new Error('Instance not found in Emoji validator.')

    const { target, message, priority, halt } = validate.emoji

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        validator.addValidator(field, rules.emoji, message, priority, halt)
      },
      instance
    )
  }

  /**
   * Rg - Is validation rg
   * @param { Validator! } instance - Instance of Validator
   */
  static Rg(instance, optional) {
    if (!instance) throw new Error('Instance not found in Rg validator.')

    const { target, message, priority, halt, mask } = validate.rg

    const fields = instance.form.querySelectorAll(target)
    this.ApplyRule(
      fields,
      (field, validator) => {
        if (mask) {
          VMasker(field).maskPattern(mask)
        }
        validator.addValidator(
          field,
          (value) => {
            return rules.rg(value, optional)
          },
          message,
          priority,
          halt
        )
      },
      instance
    )
  }

  /**
   * ApplyRule - Applies the validation rule to all related fields.
   * @param { Array<HTMLFormControlsCollection> } fields
   * @param { function! } func - is a Function to apply the rule on field in array
   * @param { Validator! } instance - is a instance of Validator
   */
  static ApplyRule(fields, func, instance) {
    if (!func) throw new Error('')
    if (!instance) throw new Error('Validator Instance not found.')
    if (fields && fields.length) {
      fields.forEach((field) => func(field, instance))
    }
  }
}

export default Validator
